import { PolarAngleAxis, RadialBar, RadialBarChart } from 'recharts';
import { telemetry } from '../../api/client';
import { gaugeBounds, reduceMatrix } from '../mapping';
import type { GaugePanelConfig } from '../types';
import { PanelEmpty, PanelError, PanelSkeleton } from './chrome';
import { usePanelQuery } from './usePanelQuery';

export interface GaugePanelProps {
  panel: GaugePanelConfig;  // options: { min?, max? }; defaults via gaugeBounds(unit)
  timeRange: string;
  refreshMs: number;
  unit?: string;
}

export function GaugePanel({ panel, timeRange, refreshMs, unit }: GaugePanelProps) {
  const { data, isLoading, isError, refetch } = usePanelQuery(
    ['telemetry', 'query', panel.metric, panel.serials],
    timeRange,
    refreshMs,
    (w) => telemetry.query({ metric: panel.metric, serials: panel.serials, ...w }),
  );

  if (isLoading) return <PanelSkeleton />;
  if (isError) return <PanelError onRetry={() => void refetch()} />;

  const value = reduceMatrix(data?.series ?? [], 'last');
  if (value === null) return <PanelEmpty />;

  const bounds = gaugeBounds(unit ?? '', panel.options);
  if (bounds === null) {
    return (
      <div className="h-full flex items-center justify-center px-4 text-center text-xs text-gray-500">
        Set a max bound for this gauge in the panel editor
      </div>
    );
  }

  return (
    <div className="relative h-full">
      <RadialBarChart
        responsive style={{ width: '100%', height: '100%' }}
        data={[{ value: Math.min(Math.max(value, bounds.min), bounds.max) }]}
        startAngle={180} endAngle={0} innerRadius="70%" outerRadius="100%" cy="75%"
      >
        <PolarAngleAxis type="number" domain={[bounds.min, bounds.max]} tick={false} />
        <RadialBar dataKey="value" background cornerRadius={8} fill="#f97316" />
      </RadialBarChart>
      <div className="absolute inset-x-0 bottom-[15%] flex flex-col items-center">
        <span className="text-3xl font-semibold text-white">
          {value >= 100 ? value.toFixed(0) : value.toFixed(1)}
        </span>
        {unit && <span className="text-xs text-gray-500">{unit}</span>}
      </div>
    </div>
  );
}
